import type { Stage } from '../kit/stage';
import { isLate } from '../time/dueDate';

/**
 * The filters every case table offers.
 *
 * One table and one filter bar everywhere, and so one rule behind each filter:
 * the same words find the same cases on Orders, on a stage and in a person's
 * record, and "overdue" means the same thing wherever a list says it.
 */

/**
 * Whether a row matches what was typed into a table's search box.
 *
 * Case-insensitive, and matched against each value on its own, so a search does
 * not match across the gap between a job order number and a doctor. An absent
 * value never matches: a caller without seeIdentity receives no doctor or
 * patient name at all, and a search over those rows must neither throw nor find
 * a name it was never sent.
 */
export function matchesSearch(
  query: string,
  values: ReadonlyArray<string | null | undefined>,
): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return values.some((value) => typeof value === 'string' && value.toLowerCase().includes(needle));
}

/**
 * Whether a case counts as overdue on the floor's lists: its due date has
 * passed, and it is not yet Ready.
 *
 * It was a day wider than that, on purpose, as a warning: due within one
 * working day read Overdue, on the order, in the figure above a stage and in
 * the Overdue only filter. The lab asked why a case that was not late said it
 * was. The request of 7 October 2026, section 6: overdue means late, here as
 * everywhere, by the one rule in src/domain/time/dueDate.ts. The figure and the
 * filter both read this, so they still cannot disagree. A case at Ready, or
 * with no stage because it has left the building, is late on no bench.
 */
/**
 * Whether an order is finished, for its due date: every tray Ready, or
 * dispatched (which leaves it no stage), or archived. A finished case is never
 * overdue, so its date keeps no colour. The request of 7 October 2026, section 6.
 *
 * An order still in production is never finished, whatever bench its first
 * tray stands at: `currentStage` is one tray's, and an order of two trays is
 * in production until both are Ready. That is how the server counts it.
 */
export function caseFinished(order: {
  currentStage: Stage | null;
  state?: string;
  archive?: { archived: boolean } | null;
}): boolean {
  if (order.archive?.archived === true) return true;
  if (order.state === 'production') return false;
  return order.currentStage === null || order.currentStage === 'ready';
}

export function listsAsOverdue(
  item: { deliveryDate: string; stage: Stage | null },
  today: string,
): boolean {
  return isLate(item.deliveryDate, today, item.stage === null || item.stage === 'ready');
}
